import { styled, css } from 'styled-components';

// ── Module shell ──

export const ResizeHint = styled.span`
  position: absolute;
  bottom: -0.3rem;
  right: -0.3rem;
  opacity: 0;
  transition: opacity 0.15s ease;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 2rem;
    height: 2rem;
  }
`;

export const ModuleShellContainer = styled.div`
  ${({ theme: { colors, spacings } }) => css`
    background: #ffffff;
    border: 1px solid ${colors.brandDarkTint90};
    border-radius: 0.5rem;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
    padding: ${spacings.s20};
    box-sizing: border-box;

    &:hover ${ResizeHint} {
      opacity: 1;
    }
  `}
`;

export const ModuleHeader = styled.div<{ $draggable?: boolean }>`
  ${({ theme: { spacings }, $draggable }) => css`
    display: flex;
    align-items: center;
    gap: ${spacings.s8};
    line-height: 1;
    flex-shrink: 0;
    cursor: ${$draggable ? 'move' : 'default'};
  `}
`;

export const ModuleTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex: 1;
  min-width: 0;
`;

export const ModuleTitle = styled.h3`
  ${({ theme: { colors, fontSizes, fontWeights } }) => css`
    font-size: ${fontSizes.s14};
    font-weight: ${fontWeights.regular};
    color: ${colors.brandDark};
    margin: 0;
    padding: 0;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

export const ModuleInfoIcon = styled.span`
  ${() => css`
    display: flex;
    align-items: center;
    flex-shrink: 0;

    svg {
      width: 1rem;
      height: 1rem;
    }
  `}
`;

export const ModuleBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const ModuleMenuButton = styled.button`
  ${({ theme: { colors } }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border: 1px solid ${colors.brandDarkTint80};
    background: ${colors.brandLight};
    border-radius: 0.375rem;
    cursor: pointer;
    color: ${colors.brandDark};
    transition:
      background 0.1s ease,
      border-color 0.1s ease;

    svg {
      width: 1rem;
      height: 1rem;
    }

    &:hover {
      background: ${colors.brandDarkTint95};
      border-color: ${colors.brandDarkTint70};
    }
  `}
`;

// ── Module no-data state ──

export const NoDataContainer = styled.div`
  ${({ theme: { colors, spacings } }) => css`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: ${colors.brandDarkAlpha50};
    text-align: center;
    padding: ${spacings.s32} ${spacings.s16};
  `}
`;

export const NoDataTitle = styled.p`
  ${({ theme: { fontSizes, fontWeights } }) => css`
    font-size: ${fontSizes.s14};
    font-weight: ${fontWeights.medium};
    margin: 0 0 0.25rem 0;
  `}
`;

export const NoDataDescription = styled.p`
  ${({ theme: { fontSizes } }) => css`
    font-size: ${fontSizes.s12};
    margin: 0;
  `}
`;

// ── Module no-profiles empty state ──

export const NoProfilesContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem 1rem;
  border-radius: 0.5rem;
`;

export const NoProfilesIcon = styled.span`
  ${({ theme: { colors } }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${colors.brandPrimary};
    margin-bottom: 0.75rem;

    svg {
      width: 2rem;
      height: 2rem;
    }
  `}
`;

export const NoProfilesTitle = styled.p`
  ${({ theme: { colors, fontSizes, fontWeights } }) => css`
    font-size: ${fontSizes.s14};
    font-weight: ${fontWeights.medium};
    color: ${colors.brandDark};
    margin: 0 0 0.25rem 0;
  `}
`;

export const NoProfilesDescription = styled.p`
  ${({ theme: { colors, fontSizes } }) => css`
    font-size: ${fontSizes.s12};
    color: ${colors.brandDarkAlpha50};
    margin: 0;
  `}
`;

// ── Module error state ──

export const ModuleErrorState = styled.div`
  ${({ theme: { colors, spacings } }) => css`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: ${colors.semanticDanger};
    text-align: center;
    padding: ${spacings.s32} ${spacings.s16};
  `}
`;

// ── Module profile list ──

// ── Module footer (profile avatars bar) ──

export const ModuleFooter = styled.div``;

export const ProfileIconsContainer = styled.div`
  ${({ theme: { spacings } }) => css`
    display: inline-flex;
    align-items: center;
    gap: ${spacings.s4};
    margin-left: auto;

    svg {
      height: 1.25rem;
      width: 1.25rem;
    }
  `}
`;

export const DragHandle = styled.span`
  position: absolute;
  left: -0.2rem;
  top: 1.75rem;
  transform: translateY(-50%);
  opacity: 0;
  transition: opacity 0.15s ease;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 1.75rem;
    height: 1.75rem;
  }

  ${ModuleShellContainer}:hover & {
    opacity: 1;
    cursor: grab;
  }

  &:active {
    cursor: grabbing;
  }
`;

export const HeaderActions = styled.div`
  position: absolute;
  right: 1rem;
  top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;

  ${ModuleShellContainer}:hover & {
    opacity: 1;
    pointer-events: auto;
  }
`;

// ── Module error ──

export const ErrorTitle = styled.p`
  ${({ theme: { fontWeights } }) => css`
    font-weight: ${fontWeights.medium};
    margin: 0 0 0.25rem 0;
  `}
`;

export const ErrorDescription = styled.p`
  ${({ theme: { colors, fontSizes } }) => css`
    font-size: ${fontSizes.s12};
    margin: 0;
    color: ${colors.brandDarkAlpha50};
  `}
`;

export const ErrorRetryWrapper = styled.div`
  margin-top: 0.75rem;
`;
